import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Toaster } from 'react-hot-toast';

// Components
import Layout from './components/Layout/Layout';
import { BATCH_OPS_ROLES } from './utils/batchOpsRoles';
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import DashboardRouter from './pages/Dashboard/DashboardRouter';
import Students from './pages/Students/Students';
import RmStudents from './pages/Rm/RmStudents';
import RmStudentDetail from './pages/Rm/RmStudentDetail';
import RmLeaveFollowups from './pages/Rm/RmLeaveFollowups';
import ParentMeetings from './pages/Rm/ParentMeetings';
import ParentMeetingCalendar from './pages/Rm/ParentMeetingCalendar';
import Teachers from './pages/Teachers/Teachers';
import Batches from './pages/Batches/Batches';
import BatchEnrollmentOptions from './pages/Batches/BatchEnrollmentOptions';
import BatchDetailsPage from './pages/Batches/BatchDetailsPage';
import ClassObservations from './pages/Teacher/ClassObservations';
import Allocations from './pages/Allocations/Allocations';
import Scheduling from './pages/Scheduling/Scheduling';
import Leads from './pages/Leads/Leads';
import WhatsAppChats from './pages/WhatsApp/WhatsAppChats';
import LeadsAnalytics from './pages/Leads/LeadsAnalytics';
import Demos from './pages/Demos/Demos';
import Reports from './pages/Reports/Reports';
import ManagementDashboard from './pages/Management/ManagementDashboard';
import StuckLeadsList from './pages/Management/StuckLeadsList';
import ManagementRankings from './pages/Management/ManagementRankings';
import DemoBreakdown from './pages/Management/DemoBreakdown';
import Profile from './pages/Profile/Profile';
import Sales from './pages/Sales/Sales';
import LMSContent from './pages/LMS/LMSContent';
import MyTraining from './pages/LMS/MyTraining';
import AssignmentManager from './pages/LMS/AssignmentManager';
import MeetingManager from './pages/Meetings/MeetingManager';
import MyMeetings from './pages/Meetings/MyMeetings';
import UserManagement from './pages/Admin/UserManagement';
import Enroll from './pages/Enroll/Enroll';
import EnrollShortRedirect from './pages/Enroll/EnrollShortRedirect';
import AccountDeletion from './pages/Legal/AccountDeletion';
import FinancePricing from './pages/Finance/FinancePricing';
import FinanceSubscriptions from './pages/Finance/FinanceSubscriptions';
import SubscriptionDetails from './pages/Finance/SubscriptionDetails';
import AdmissionStudents from './pages/Admission/AdmissionStudents';
import AdmissionStudentAttendance from './pages/Admission/AdmissionStudentAttendance';
import AttendanceMark from './pages/Attendance/AttendanceMark';
import TeacherHome from './pages/Teacher/TeacherHome';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import TeacherSessionDetail from './pages/Teacher/TeacherSessionDetail';
import TeacherLeave from './pages/Teacher/TeacherLeave';
import ManagerLeaveApprovals from './pages/Teacher/ManagerLeaveApprovals';
import TeacherMakeupClasses from './pages/Teacher/TeacherMakeupClasses';
import TeacherSubstitutionHistory from './pages/Teacher/TeacherSubstitutionHistory';
import TeacherStudentAttendance from './pages/Teacher/TeacherStudentAttendance';
import TeacherTimetable from './pages/Teacher/TeacherTimetable';
import TeacherUpcomingBatches from './pages/Teacher/TeacherUpcomingBatches';
import TeacherSessionApprovals from './pages/Teacher/TeacherSessionApprovals';
import TeacherHomeworkSession from './pages/Teacher/TeacherHomeworkSession';
import TeacherMonthlyReport from './pages/Teacher/TeacherMonthlyReport';
import TeacherStudentTaught from './pages/Teacher/TeacherStudentTaught';
import TimesheetPage from './pages/Timesheets/TimesheetPage';
import TimesheetInboxPage from './pages/Timesheets/TimesheetInboxPage';
import StaffLeavePage from './pages/Leave/StaffLeavePage';
import StaffLeaveInboxPage from './pages/Leave/StaffLeaveInboxPage';
import StaffLeaveSetupPage from './pages/Leave/StaffLeaveSetupPage';
import PayMonthPage from './pages/Payroll/PayMonthPage';
import PaySetupPage from './pages/Payroll/PaySetupPage';
import StaffProfilePage from './pages/Payroll/StaffProfilePage';
import PayslipPage from './pages/Payroll/PayslipPage';
import TicketsPage from './pages/Tickets/TicketsPage';
import TicketCreatePage from './pages/Tickets/TicketCreatePage';
import TicketDetailPage from './pages/Tickets/TicketDetailPage';
import HelpCenterPage from './pages/Help/HelpCenterPage';
import HelpAdminPage from './pages/Help/HelpAdminPage';
import SupervisorMonthlyReportsHub from './pages/Teacher/SupervisorMonthlyReportsHub';
import StudentMonthlyReports from './pages/Teacher/StudentMonthlyReports';
import StudentMonthlyReportForm from './pages/Teacher/StudentMonthlyReportForm';
import TeachingHandover from './pages/Teacher/TeachingHandover';
import AssessmentCatalogAdmin from './pages/Teacher/AssessmentCatalogAdmin';
import TeacherSupervisorRoster from './pages/Teacher/TeacherSupervisorRoster';
import ParentReportPublicView from './pages/Public/ParentReportPublicView';
import NewsPage from './pages/News/NewsPage';
import FamilyWelcome from './pages/Family/FamilyWelcome';
import FamilyShell from './pages/Family/FamilyShell';
import FamilyHome from './pages/Family/FamilyHome';
import FamilyClasses from './pages/Family/FamilyClasses';
import FamilyClassDetail from './pages/Family/FamilyClassDetail';
import FamilyProgress from './pages/Family/FamilyProgress';
import FamilyHomework from './pages/Family/FamilyHomework';
import FamilyHomeworkSubmit from './pages/Family/FamilyHomeworkSubmit';
import FamilyMessages from './pages/Family/FamilyMessages';
import FamilyCompose from './pages/Family/FamilyCompose';
import FamilyThread from './pages/Family/FamilyThread';
import FamilyLeave from './pages/Family/FamilyLeave';
import FamilyAccount from './pages/Family/FamilyAccount';
import FamilyStaffInbox from './pages/Family/FamilyStaffInbox';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, hasRole, isFamilyPortal } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isFamilyPortal()) {
    return <Navigate to="/family" replace />;
  }

  if (allowedRoles.length > 0 && !hasRole(allowedRoles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const FamilyProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isFamilyPortal } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isFamilyPortal()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const TeachingHomeRoute = () => {
  const { isCountryManager } = useAuth();
  if (isCountryManager()) return <TeacherHome />;
  return <Navigate to="/dashboard" replace />;
};

/** Sales uses My students (/admission) as the student hub — redirect away from /students. */
const StudentsRoute = () => {
  const { isSales } = useAuth();
  if (isSales()) return <Navigate to="/admission" replace />;
  return <Students />;
};

// Theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Navigate to="/login" replace />} />
                <Route path="/register/student" element={<Navigate to="/login" replace />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/family/welcome" element={<FamilyWelcome />} />
                <Route path="/family/login" element={<Navigate to="/login" replace />} />
                <Route
                  path="/family"
                  element={
                    <FamilyProtectedRoute>
                      <FamilyShell />
                    </FamilyProtectedRoute>
                  }
                >
                  <Route index element={<FamilyHome />} />
                  <Route path="classes" element={<FamilyClasses />} />
                  <Route path="classes/:sessionId" element={<FamilyClassDetail />} />
                  <Route path="homework" element={<FamilyHomework />} />
                  <Route path="homework/:sessionId" element={<FamilyHomeworkSubmit />} />
                  <Route path="progress" element={<FamilyProgress />} />
                  <Route path="messages" element={<FamilyMessages />} />
                  <Route path="messages/new" element={<FamilyCompose />} />
                  <Route path="messages/:threadId" element={<FamilyThread />} />
                  <Route path="leave" element={<FamilyLeave />} />
                  <Route path="more" element={<FamilyAccount />} />
                  <Route path="account" element={<FamilyAccount />} />
                </Route>
                <Route path="/e/:code" element={<EnrollShortRedirect />} />
                <Route path="/enroll" element={<Enroll />} />
                <Route path="/account-deletion" element={<AccountDeletion />} />
                <Route path="/public/parent-report/:token" element={<ParentReportPublicView />} />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardRouter />} />
                  <Route path="news" element={<NewsPage />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route
                    path="students"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager', 'teacher_supervisor', 'teacher_admin', 'finance_supervisor', 'parent_feedback_agent', ...BATCH_OPS_ROLES]}>
                        <StudentsRoute />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="rm"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager']}>
                        <RmStudents />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="rm/leave-followups"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager']}>
                        <RmLeaveFollowups />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="rm/parent-meetings"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager']}>
                        <ParentMeetings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="rm/parent-meetings/calendar"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager']}>
                        <ParentMeetingCalendar />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="rm/:studentId"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager']}>
                        <RmStudentDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teachers"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_supervisor', 'teacher_admin', 'country_manager', ...BATCH_OPS_ROLES]}>
                        <Teachers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="batches"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher', 'sales', 'sales_supervisor', 'country_manager', 'teacher_supervisor', 'teacher_admin', ...BATCH_OPS_ROLES]}>
                        <Batches />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="batch-options"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager', 'teacher_supervisor', 'teacher_admin', ...BATCH_OPS_ROLES]}>
                        <BatchEnrollmentOptions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teaching/class-observations"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_supervisor', 'teacher_admin']}>
                        <ClassObservations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="batches/create"
                    element={<Navigate to="/batches?create=1" replace />}
                  />
                  <Route
                    path="batches/:batchId"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher', 'teacher_supervisor', 'teacher_admin', 'sales', 'sales_supervisor', 'country_manager', ...BATCH_OPS_ROLES]}>
                        <BatchDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="allocations"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'teacher_supervisor', 'teacher_admin', ...BATCH_OPS_ROLES]}>
                        <Allocations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="leads"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager', 'teacher_admin']}>
                        <Leads />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="whatsapp"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager']}>
                        <WhatsAppChats />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="leads/analytics"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager']}>
                        <LeadsAnalytics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="leads/stuck-leads"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager']}>
                        <StuckLeadsList backPath="/leads/analytics" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="demos"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher', 'sales', 'sales_supervisor', 'country_manager', 'teacher_supervisor', 'teacher_admin']}>
                        <Demos />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/home"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin', 'country_manager', 'admin', ...BATCH_OPS_ROLES]}>
                        <TeachingHomeRoute />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/timetable"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin']}>
                        <TeacherTimetable />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/upcoming-batches"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin', 'admin']}>
                        <TeacherUpcomingBatches />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/sessions/:sessionId"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin', 'country_manager', 'admin', ...BATCH_OPS_ROLES]}>
                        <TeacherSessionDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/leave"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin', 'admin']}>
                        <TeacherLeave />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/student-attendance"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin', 'finance_supervisor', 'admin']}>
                        <TeacherStudentAttendance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/substitution-history"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'teacher',
                          'teacher_supervisor',
                          'teacher_admin',
                          'country_manager',
                          'admin',
                        ]}
                      >
                        <TeacherSubstitutionHistory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/makeup-classes"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'teacher',
                          'teacher_supervisor',
                          'teacher_admin',
                          'country_manager',
                          'admin',
                        ]}
                      >
                        <TeacherMakeupClasses />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/leave-approvals"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_supervisor', 'teacher_admin', 'country_manager']}>
                        <ManagerLeaveApprovals />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/approvals"
                    element={
                      <ProtectedRoute allowedRoles={['teacher_supervisor', 'teacher_admin']}>
                        <TeacherSessionApprovals />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/homework"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin']}>
                        <TeacherHomeworkSession />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="timesheets"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'teacher',
                          'teacher_supervisor',
                          'teacher_admin',
                          'sales',
                          'sales_supervisor', 'country_manager',
                          'finance_user',
                          'finance_supervisor',
                          'finance_admin',
                          'parent_feedback_agent',
                          ...BATCH_OPS_ROLES,
                        ]}
                      >
                        <TimesheetPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="leave"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'sales',
                          'sales_supervisor', 'country_manager',
                          'sales_admin',
                          'finance_user',
                          'finance_supervisor',
                          'finance_admin',
                          'parent_feedback_agent',
                          'teacher',
                          'teacher_supervisor',
                          'teacher_admin',
                          ...BATCH_OPS_ROLES,
                        ]}
                      >
                        <StaffLeavePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="leave/inbox"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'sales_supervisor', 'country_manager',
                          'sales_admin',
                          'finance_supervisor',
                          'finance_admin',
                          'teacher_supervisor',
                          'teacher_admin',
                          'admin',
                        ]}
                      >
                        <StaffLeaveInboxPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="payroll"
                    element={
                      <ProtectedRoute allowedRoles={['finance_supervisor', 'finance_admin', 'admin']}>
                        <PayMonthPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="payroll/setup"
                    element={
                      <ProtectedRoute allowedRoles={['finance_supervisor', 'admin']}>
                        <PaySetupPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="payroll/people"
                    element={
                      <ProtectedRoute allowedRoles={['finance_supervisor', 'admin']}>
                        <StaffProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="payroll/me"
                    element={
                      <ProtectedRoute>
                        <PayslipPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="leave/setup"
                    element={
                      <ProtectedRoute allowedRoles={['finance_supervisor', 'admin']}>
                        <StaffLeaveSetupPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="timesheets/inbox"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'teacher_supervisor',
                          'teacher_admin',
                          'sales_supervisor', 'country_manager',
                          'sales_admin',
                          'finance_supervisor',
                          'finance_admin',
                          'admin',
                        ]}
                      >
                        <TimesheetInboxPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="tickets"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'parent_feedback_agent',
                          'sales',
                          'sales_supervisor', 'country_manager',
                          'teacher',
                          'teacher_supervisor',
                          'teacher_admin',
                          'finance_admin',
                          'admin',
                          ...BATCH_OPS_ROLES,
                        ]}
                      >
                        <TicketsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/family-messages"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin', 'admin']}>
                        <FamilyStaffInbox channel="teacher" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="rm/family-messages"
                    element={
                      <ProtectedRoute allowedRoles={['sales', 'sales_supervisor', 'country_manager', 'admin']}>
                        <FamilyStaffInbox channel="rm" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="tickets/inbox"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'parent_feedback_agent',
                          'sales',
                          'sales_supervisor', 'country_manager',
                          'teacher',
                          'teacher_supervisor',
                          'teacher_admin',
                          'finance_admin',
                          'admin',
                          ...BATCH_OPS_ROLES,
                        ]}
                      >
                        <TicketsPage inboxDefault />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="help"
                    element={
                      <ProtectedRoute>
                        <HelpCenterPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="help/article/:articleId"
                    element={
                      <ProtectedRoute>
                        <HelpCenterPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="help/admin"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <HelpAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="tickets/new"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'parent_feedback_agent',
                          'sales',
                          'sales_supervisor', 'country_manager',
                          'teacher',
                          'teacher_supervisor',
                          'admin',
                          'teacher_admin',
                          ...BATCH_OPS_ROLES,
                        ]}
                      >
                        <TicketCreatePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="tickets/:id"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          'parent_feedback_agent',
                          'sales',
                          'sales_supervisor', 'country_manager',
                          'teacher',
                          'teacher_supervisor',
                          'teacher_admin',
                          'finance_admin',
                          'admin',
                          ...BATCH_OPS_ROLES,
                        ]}
                      >
                        <TicketDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/monthly-report"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin']}>
                        <TeacherMonthlyReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/what-was-taught"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin']}>
                        <TeacherStudentTaught />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/monthly-reports-inbox"
                    element={
                      <ProtectedRoute allowedRoles={['teacher_supervisor', 'teacher_admin', 'admin', 'sales_supervisor', 'country_manager', 'sales', 'parent_feedback_agent']}>
                        <SupervisorMonthlyReportsHub />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/student-reports"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin']}>
                        <StudentMonthlyReports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/student-reports/:studentId/:month"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin']}>
                        <StudentMonthlyReportForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/students/:studentId/teaching-handover"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'teacher_supervisor', 'teacher_admin']}>
                        <TeachingHandover />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/assessment-catalog"
                    element={
                      <ProtectedRoute allowedRoles={['teacher_admin', 'admin']}>
                        <AssessmentCatalogAdmin />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="teacher/supervisor-roster"
                    element={
                      <ProtectedRoute allowedRoles={['teacher_admin', 'admin']}>
                        <TeacherSupervisorRoster />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="attendance/session/:sessionId"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher', 'teacher_supervisor', 'teacher_admin']}>
                        <AttendanceMark />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="scheduling"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'sales', 'sales_supervisor', 'country_manager', 'teacher_supervisor', 'teacher_admin', ...BATCH_OPS_ROLES]}>
                        <Scheduling />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="sales"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <Sales />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="finance"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'finance_user', 'finance_supervisor', 'finance_admin']}>
                        <FinanceSubscriptions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="finance/subscriptions/:id"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'finance_user', 'finance_supervisor', 'finance_admin']}>
                        <SubscriptionDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="finance/pricing"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'finance_user', 'finance_supervisor', 'finance_admin']}>
                        <FinancePricing />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admission/student-attendance"
                    element={
                      <ProtectedRoute allowedRoles={['country_manager', 'admin']}>
                        <AdmissionStudentAttendance />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admission"
                    element={
                      <ProtectedRoute allowedRoles={['sales', 'sales_supervisor', 'country_manager', 'admin']}>
                        <AdmissionStudents />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admission/subscriptions/:id"
                    element={
                      <ProtectedRoute allowedRoles={['sales', 'sales_supervisor', 'country_manager', 'admin']}>
                        <SubscriptionDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="lms/content"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_supervisor', 'teacher_admin']}>
                        <LMSContent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="lms/assignments"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_supervisor', 'teacher_admin']}>
                        <AssignmentManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="meetings"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_supervisor', 'teacher_admin']}>
                        <MeetingManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="meetings/mine"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'admin', 'teacher_supervisor', 'teacher_admin']}>
                        <MyMeetings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="lms/my-content"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'admin', 'teacher_supervisor', 'teacher_admin']}>
                        <MyTraining contentType="teacher_training" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="lms/class-content"
                    element={
                      <ProtectedRoute allowedRoles={['teacher', 'admin', 'teacher_supervisor', 'teacher_admin']}>
                        <MyTraining contentType="student_teaching" />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="reports"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_supervisor', 'teacher_admin']}>
                        <Reports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="management"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_admin']}>
                        <ManagementDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="management/stuck-leads"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <StuckLeadsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="management/rankings"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'teacher_admin']}>
                        <ManagementRankings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="management/demos/:id"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <DemoBreakdown />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/users"
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <UserManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="profile" element={<Profile />} />
                </Route>

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </div>
          </AuthProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#4caf50',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#f44336',
                  secondary: '#fff',
                },
              },
            }}
          />
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App; 